import React, { useEffect, useState, useCallback } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { canDeleteForever } from '../utils/deleteGuard';
import AccountBar from './account/AccountBar';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import AccountMetrics from './account/AccountMetrics';
import { clientMetrics } from './account/accountMetricSets';
import { useExport } from './header/exporters';
import { isArchiveView, toArchiveView, type ArchiveView } from '../utils/archiveFilter';
import LoadErrorBox from './ui/LoadErrorBox';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import DataTable, { Column, actionsColumn } from './DataTable';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './EmptyState';
import { useNotifications } from './NotificationProvider';
import { useRole } from '../hooks/useRole';
import {
  loadClients,
  deleteClient,
  restoreClient,
  Client,
} from '../services/ClientService';
import { NEW_CLIENT_PATH, clientPath } from '../utils/accountRoutes';
import '../styles/components/PipelineView.css';
import '../styles/components/ClientsView.css';

// Account-level Clients list — sits alongside Sales Pipeline/Projects in
// AccountShell, which (unlike the per-project Sidebar+Header shell) renders
// no Header.tsx at all. So this page owns its own toolbar (title, search,
// archive filter, New Client button) directly, the same way PipelineView
// does, rather than routing those controls through Header's breadcrumb bar.
const ClientsView: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  // Viewers can read clients but not change them (the API answers 403).
  const { canEdit } = useRole();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveView>('active');

  const [archiveTarget, setArchiveTarget] = useState<Client | null>(null);
  const [archiving, setArchiving] = useState(false);

  // The backend's `search` param is server-side, but this page loads the
  // whole (archive-filtered) list and leaves free-text narrowing to the header
  // search (DataTable applies it client-side), so typing doesn't round-trip
  // the API on every keystroke.
  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    loadClients('', archiveFilter)
      .then(setClients)
      // Shown in the page with a Retry, not as a toast that vanishes and
      // leaves what looks like an empty list.
      .catch(err => setLoadError(err ?? new Error('load failed')))
      .finally(() => setLoading(false));
  }, [archiveFilter, showError]);

  useEffect(() => { reload(); }, [reload]);

  // Export button: the clients listed right now.
  useExport('account-clients', { rows: loading || loadError ? [] : clients, ctx: undefined });

  const openNew = () => navigate(NEW_CLIENT_PATH);
  const openEdit = (c: Client) => navigate(clientPath(c.id));

  const handleArchive = async () => {
    if (!archiveTarget || archiving) return;
    setArchiving(true);
    try {
      await deleteClient(archiveTarget.id);
      showSuccess(`${archiveTarget.name} archived.`);
      setArchiveTarget(null);
      reload();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'client', action: 'archive' }));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (c: Client) => {
    try {
      await restoreClient(c.id);
      showSuccess(`${c.name} restored.`);
      reload();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'client', action: 'restore' }));
    }
  };

  // Delete forever is only offered on an archived record (the server refuses
  // otherwise); the dialog says why if it fails.
  const forever = useDeleteConfirm<Client>({
    record: 'client',
    perform: row => deleteClient(row.id, true),
    onDone: () => { showSuccess('Client deleted.'); reload(); },
  });

  const columns: Column<Client>[] = [
    idColumn<Client>('client'),
    { key: 'name', header: 'Name', sortable: true, className: 'cl-col-name' },
    { key: 'email', header: 'Email', className: 'cl-col-email', render: v => v || '—' },
    { key: 'phone', header: 'Phone', className: 'cl-col-phone', render: v => v || '—' },
    { key: 'project_count', header: 'Projects', width: '110px', align: 'center' },
    actionsColumn<Client>([
      { key: 'edit', icon: 'edit', title: 'Edit', onClick: openEdit, show: () => canEdit && archiveFilter !== 'archived' },
      { key: 'archive', icon: 'trash', title: 'Archive', variant: 'danger', onClick: setArchiveTarget, show: () => canEdit && archiveFilter !== 'archived' },
      { key: 'delete-forever', icon: 'trash', title: 'Delete forever', variant: 'danger', onClick: forever.request, show: row => canEdit && canDeleteForever(row) },
      { key: 'restore', icon: 'refresh', title: 'Restore', onClick: handleRestore, show: row => canEdit && !!row.archived },
    ]),
  ];

  // The page title is the shell's header title; New and the Archived switch
  // sit in the header bar beside it, as on the project screens.
  const barActions: BreadcrumbAction[] = canEdit
    ? [{ kind: 'primary', key: 'new', icon: 'plus', label: 'New Client', onClick: openNew }]
    : [];

  return (
    <div className="wt-container fade-in">
        <AccountBar
          view="clients"
          archived={{ checked: isArchiveView(archiveFilter), onChange: on => setArchiveFilter(toArchiveView(on)) }}
          actions={<BreadcrumbBar actions={barActions} />}
          exportKey="account-clients"
        />

        {archiveFilter !== 'archived' && !loadError && !loading && clients.length > 0 && (
          <AccountMetrics label="Clients summary" metrics={clientMetrics(clients)} />
        )}

        {loadError ? (
          <LoadErrorBox what="clients" error={loadError} onRetry={reload} retrying={loading} />
        ) : !loading && clients.length === 0 ? (
          <EmptyState
            icon="users"
            title={archiveFilter === 'archived' ? 'No archived clients' : 'No clients yet'}
            description={archiveFilter === 'archived'
              ? 'Clients you archive will show up here.'
              : 'Add the couples and families you work with to keep their contact details in one place.'}
            action={canEdit && archiveFilter !== 'archived' ? (
              <button type="button" className="pl-new-btn" onClick={openNew}>
                <Icon name="plus" size={14} /> Add first client
              </button>
            ) : undefined}
          />
        ) : (
          <DataTable<Client>
            className="cl-table"
            recordList={{ kind: 'client' }}
            data={clients}
            columns={columns}
            loading={loading}
            hoverable
            onRowClick={row => { if (canEdit && archiveFilter !== 'archived') openEdit(row); }}
            emptyMessage="No clients match your search."
          />
        )}

      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this client forever?"
        subject={forever.target?.name}
        description="This cannot be undone."
        consequences={['The client and their contact details', 'Their notes']}
        confirmLabel="Delete forever"
      />
      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title={`Archive "${archiveTarget?.name}"?`}
        description="It will be hidden from the active list but not permanently deleted."
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  );
};

export default ClientsView;
